import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { FrontpageService } from '@/services/frontpage.service'
import { useLocaleStore } from '@/stores/locale.store'
import type { Locale } from '@/router/locales'

import type { Frontpage } from '@/models/frontpage'

type ResourceKey = 'frontpage'
type CacheMeta = { fetchedAt: number; ttlMs: number; locale: Locale | null }
const now = () => Date.now()

export const useFrontpageStore = defineStore('frontpage', () => {
  const localeStore = useLocaleStore()
  const frontpage = ref<Frontpage | null>(null)

  // Status
  const isLoading = ref(false) // Only use this if you want to block something globally on the front page.
  const loading = ref<Partial<Record<ResourceKey, boolean>>>({})
  const errors = ref<Partial<Record<ResourceKey, unknown>>>({})
  const etag = ref<string | null>(null)

  const hasErrors = computed(() => Object.values(errors.value).some((e) => e != null))

  // Cache
  const cache = ref<Record<ResourceKey, CacheMeta>>({
    frontpage: { fetchedAt: 0, ttlMs: 5 * 60_000, locale: null }, // 5 min (adjust if needed)
  })

  const inFlight = new Map<ResourceKey, Promise<void>>()

  function isFresh(key: ResourceKey) {
    const meta = cache.value[key]
    return (
      meta.fetchedAt > 0 &&
      meta.locale === localeStore.currentLocale &&
      now() - meta.fetchedAt < meta.ttlMs
    )
  }

  function markFetched(key: ResourceKey) {
    cache.value[key].fetchedAt = now()
    cache.value[key].locale = localeStore.currentLocale
  }

  async function load(opts?: { force?: boolean }) {
    const key: ResourceKey = 'frontpage'
    if (!opts?.force && isFresh(key)) return

    const existing = inFlight.get(key)
    if (existing) return existing

    loading.value[key] = true
    errors.value[key] = undefined

    if (
      cache.value[key].locale !== null
      && cache.value[key].locale !== localeStore.currentLocale
    ) {
      etag.value = null
    }

    const p = (async () => {
      try {
        const result = await FrontpageService.resource(etag.value, frontpage.value)
        if (!result.notModified) {
          frontpage.value = result.data
        }
        etag.value = result.etag
        markFetched(key)
      } catch (e) {
        errors.value[key] = e
      } finally {
        loading.value[key] = false
        inFlight.delete(key)
      }
    })()

    inFlight.set(key, p)
    return p
  }

  async function ensure(opts?: { force?: boolean }) {
    // If you want a full preloader only on app init,
    // keep the global isLoading in content.store. Here we only use a local one.
    isLoading.value = true
    try {
      await load(opts)
    } finally {
      isLoading.value = false
    }
  }

  function invalidate() {
    cache.value.frontpage.fetchedAt = 0
    cache.value.frontpage.locale = null
    etag.value = null
  }

  /** Restore the front-page response embedded by Vite SSG. */
  function hydrate(snapshot: Frontpage, snapshotEtag: string | null = null) {
    frontpage.value = snapshot
    etag.value = snapshotEtag
    errors.value.frontpage = undefined
    loading.value.frontpage = false
    markFetched('frontpage')
  }

  /** Revalidate hydrated front-page settings without a global loader. */
  async function revalidate() {
    await load({ force: true })
  }

  return {
    frontpage,
    isLoading,
    loading,
    errors,
    etag,
    hasErrors,
    hydrate,
    revalidate,
    load,
    ensure,
    invalidate,
  }
})
